import React, { useEffect, useRef, useState } from "react";
import { Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { shortDate } from "@/lib/dates";
import { useRolePermissions } from "@/hooks/useRolePermissions";

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];

const fmtSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const DocumentsTab = ({ debtorId }) => {
  const { canMutateDebtors } = useRolePermissions();
  const inputRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/debtors/${debtorId}/documents`);
      setDocs(data.documents || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debtorId]);

  const onFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${file.name}: unsupported file type`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("debtor_id", debtorId);
      try {
        await apiClient.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    setUploading(false);
    load();
  };

  const removeDoc = async (fileId, filename) => {
    try {
      await apiClient.delete(`/debtors/${debtorId}/documents/${fileId}`);
      toast.success(`Removed ${filename}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold text-[16px]">Documents</h3>
        {canMutateDebtors && (
          <>
            <JeaButton onClick={() => inputRef.current?.click()} loading={uploading} className="gap-2" data-testid="documents-upload-cta">
              <Upload size={16} /> Upload Document
            </JeaButton>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ALLOWED_EXT.join(",")}
              className="hidden"
              onChange={(e) => onFiles(Array.from(e.target.files || []))}
              data-testid="documents-file-input"
            />
          </>
        )}
      </div>

      {docs.length === 0 ? (
        <EmptyState
          icon={Upload}
          title="No documents"
          description={canMutateDebtors
            ? "Upload court orders, judgment filings, and supporting documents here."
            : "No documents have been uploaded yet."}
          testid="documents-empty-state"
          footer={
            canMutateDebtors ? (
              <JeaButton onClick={() => inputRef.current?.click()} className="gap-2 mt-2" data-testid="documents-empty-upload">
                <Upload size={16} /> Upload Document
              </JeaButton>
            ) : null
          }
        />
      ) : (
        <ul className="space-y-2" data-testid="documents-list">
          {docs.map((d) => (
            <li
              key={d.file_id}
              className="bg-bg-secondary border border-border-default rounded-[8px] px-4 py-3 flex items-center gap-4"
              data-testid={`document-item-${d.file_id}`}
            >
              <FileText size={18} className="text-accent-pink shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-[14px] font-medium truncate">{d.filename}</div>
                <div className="text-[12px] text-text-secondary">{fmtSize(d.size)}</div>
              </div>
              <div className="text-[12px] jea-date">{shortDate(d.uploaded_at)}</div>
              {canMutateDebtors && (
                <button
                  type="button"
                  onClick={() => removeDoc(d.file_id, d.filename)}
                  className="p-2 rounded text-text-secondary hover:text-danger transition-colors"
                  aria-label="Remove document"
                  data-testid={`document-remove-${d.file_id}`}
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DocumentsTab;
